import { useState } from 'react';
import { UMIcon } from './UMIcon';
import { UMR, UM_MAPBOX_STYLES, type UMBasemapId } from './referenceTokens';
import lightThumb from './assets/basemap-light.jpg';
import streetsThumb from './assets/basemap-streets.jpg';
import satelliteThumb from './assets/basemap-satellite.jpg';
import outdoorsThumb from './assets/basemap-outdoors.jpg';

const THUMBS: Record<UMBasemapId, string> = {
  light: lightThumb,
  streets: streetsThumb,
  satellite: satelliteThumb,
  outdoors: outdoorsThumb,
};

function ControlButton({ icon, label, pressed, iconFamily = 'symbols', onClick }: { icon: string; label: string; pressed?: boolean; iconFamily?: 'symbols' | 'icons'; onClick?: () => void }) {
  const [state, setState] = useState<'idle' | 'hover' | 'active'>('idle');
  const bg = state === 'active' ? UMR.active : state === 'hover' || pressed ? UMR.hover : 'transparent';
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      aria-pressed={pressed}
      onClick={onClick}
      onMouseEnter={() => setState('hover')}
      onMouseLeave={() => setState('idle')}
      onMouseDown={() => setState('active')}
      onMouseUp={() => setState('hover')}
      style={{
        width: UMR.size.controlButton,
        height: UMR.size.controlButton,
        border: 'none',
        padding: 0,
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontFamily: UMR.font,
        fontSize: 14,
        color: UMR.black,
      }}
    >
      <UMIcon name={icon} size={24} family={iconFamily} color={UMR.black} style={{ height: 31 }} />
    </button>
  );
}

function Group({ children }: { children: React.ReactNode }) {
  return <div style={{ display: 'flex', border: `1px solid ${UMR.border}`, background: UMR.white }}>{children}</div>;
}

function BasemapTile({ id, onClick }: { id: UMBasemapId; onClick: () => void }) {
  const [hover, setHover] = useState(false);
  const label = UM_MAPBOX_STYLES.find((s) => s.id === id)?.label ?? id;
  return (
    <div style={{ height: UMR.size.basemapTile, border: `1px solid ${UMR.border}`, background: UMR.white }}>
      <button
        type="button"
        onClick={onClick}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          width: 98, height: 98, padding: 0, border: 'none', display: 'flex', flexDirection: 'column',
          background: hover ? UMR.hover : UMR.white, cursor: 'pointer', textAlign: 'center',
          fontFamily: UMR.font, fontSize: 14, color: UMR.black,
        }}
      >
        <img src={THUMBS[id]} alt={id} style={{ flexGrow: 1, width: '100%', minHeight: 0, objectFit: 'cover' }} />
        <span style={{ width: '100%', height: 29, boxSizing: 'border-box', padding: 4, lineHeight: '21px' }}>{label}</span>
      </button>
    </div>
  );
}

export interface UMMapControlsProps {
  basemap: UMBasemapId;
  /** Basemaps that can be chosen. Without Mapbox token only "light" (offline fallback) is meaningful. */
  availableBasemaps?: UMBasemapId[];
  onBasemapChange?: (id: UMBasemapId) => void;
  onZoomIn?: () => void;
  onZoomOut?: () => void;
  is3d?: boolean;
  onToggle3d?: (is3d: boolean) => void;
}

/** Bottom-left map controls: basemap tile, zoom −/+, 2D/3D (positions measured in production). */
export function UMMapControls({
  basemap,
  availableBasemaps = ['light'],
  onBasemapChange,
  onZoomIn,
  onZoomOut,
  is3d = false,
  onToggle3d,
}: UMMapControlsProps) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{
        position: 'absolute', bottom: UMR.size.controlsInset, left: UMR.size.controlsInset,
        display: 'flex', alignItems: 'flex-end', gap: 8, pointerEvents: 'none', zIndex: 10,
      }}
    >
      <div style={{ display: 'flex', gap: 8, pointerEvents: 'auto' }}>
        <BasemapTile id={basemap} onClick={() => setOpen((v) => !v && availableBasemaps.length > 1)} />
        {open && availableBasemaps.filter((b) => b !== basemap).map((b) => (
          <BasemapTile key={b} id={b} onClick={() => { onBasemapChange?.(b); setOpen(false); }} />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, pointerEvents: 'auto' }}>
        <Group>
          <ControlButton icon="zoom_out" iconFamily="icons" label="Réduire le zoom" onClick={onZoomOut} />
          <ControlButton icon="zoom_in" iconFamily="icons" label="Augmenter le zoom" onClick={onZoomIn} />
        </Group>
        <Group>
          <ControlButton icon="2d_2" label="Vue 2D" pressed={!is3d} onClick={() => onToggle3d?.(false)} />
          <ControlButton icon="3d_2" label="Vue 3D" pressed={is3d} onClick={() => onToggle3d?.(true)} />
        </Group>
      </div>
    </div>
  );
}
